"use client";

import { propertyTypeOptions } from "@/info/unit/property-types";
import { Filters } from "@/types/filters";
import { useEffect, useMemo, useRef } from "react";
import { Label } from "react-aria-components";
import type { Control, UseFormSetValue } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { RadioGroupInput } from "../rac/radio-group-input";
import type { PropertyType } from "../search/filters/desktop";
import { PropertyTypeIconGrid } from "./property-type-icon-grid";
import { SwitchField } from "./switch-field";

// Define the structure of property type options for better type safety
interface PropertyTypeOption {
	value: string;
	label: string;
	finalValue?: Record<string, unknown>;
	options?: PropertyTypeOption[];
	isCompleted?: boolean;
}

// More flexible interface that works with different form structures
interface PropertyTypeFieldProps {
	control: Control<Filters>;
	name?: keyof Filters | string; // Name of the property type field in the form
	exactPropertyTypeName?: keyof Filters | string; // Name of the exact property type field
	isCommunityFieldName?: keyof Filters | string; // Name of the isCommunity field
	setValue: UseFormSetValue<Filters>;
	label?: string;
	required?: boolean;
	excludeOnly?: boolean; // If true, excludes the "Only" option from the property type selectio
	exactTypeRequired?: boolean; // If true, the exact property type is required
	hideLabel?: boolean;
	options?: typeof propertyTypeOptions;
	markFieldDirty?: (fieldName: keyof Filters) => void; // Hack to manually mark fields as dirty
}

export function PropertyTypeField({
	control,
	name = "propertyType",
	exactPropertyTypeName,
	isCommunityFieldName = "isCommunity",
	excludeOnly,
	setValue,
	label = "Property type",
	required,
	exactTypeRequired,
	hideLabel = false,
	options = propertyTypeOptions,
	markFieldDirty,
}: PropertyTypeFieldProps) {
	const typeValue: PropertyType = useWatch({ control, name: name as keyof Filters }) as unknown as PropertyType;
	const exactTypeValue = useWatch({ control, name: exactPropertyTypeName as keyof Filters }) as string;
	const previousTypeValue = useRef<PropertyType | undefined>(typeValue);

	const exactPropertyTypeOptions = useMemo(() => {
		return propertyTypeOptions.reduce(
			(acc, { value, options }) => {
				acc[value] = options;
				return acc;
			},
			{} as Record<string, PropertyTypeOption[]>,
		);
	}, []);

	// Reset subtype when property type changes and set to "Any" by default
	useEffect(() => {
		if (previousTypeValue.current !== typeValue && exactPropertyTypeName) {
			if (typeValue) {
				// When a property type is selected, set exact type to "Any" by default
				const anyOption = exactPropertyTypeOptions[typeValue]?.find(opt => opt.label.toLowerCase().includes("any"));
				if (anyOption) {
					setValue(exactPropertyTypeName as keyof Filters, anyOption.value, { shouldDirty: true });
				}
			} else {
				// When property type is cleared, clear exact type too
				setValue(exactPropertyTypeName as keyof Filters, "", { shouldDirty: true });
			}
		}
		previousTypeValue.current = typeValue;
	}, [typeValue, setValue, exactPropertyTypeName, exactPropertyTypeOptions]);
	const subTypeOptions = excludeOnly
		? (exactPropertyTypeOptions[typeValue] || []).filter(opt => !["a", "h", "v", "p"].includes(opt.value))
		: (exactPropertyTypeOptions[typeValue] || []).filter(opt => !opt.label.toLowerCase().includes("any"));
	return (
		<div className="flex flex-col py-4 my-4 gap-2">
			{!hideLabel && <Label className="text-title-medium">{label}</Label>}

			{/* Property Type Icons (Controller) */}
			<Controller
				control={control}
				name={name as keyof Filters}
				rules={{ required: required ? "Please select a property type" : false }}
				render={({ field: { value, onChange: _onChange }, fieldState }) => (
					<div className="flex flex-col gap-2">
						<PropertyTypeIconGrid
							options={options}
							required={required}
							// description="Select the main type of your property (e.g., Apartment, House, Villa, Plot). This helps buyers quickly identify the category of your listing."
							errorMessage={fieldState.error?.message}
							value={value as PropertyType}
							onChange={newValue => {
								setValue(name as keyof Filters, newValue as PropertyType, { shouldDirty: true, shouldTouch: true });
								// Hack: manually mark field as dirty since React Hook Form isn't working
								if (markFieldDirty) {
									markFieldDirty(name as keyof Filters);
								}
							}}
						/>
					</div>
				)}
			/>
			{/* Subtype as Radio Options */}
			{exactPropertyTypeName && typeValue && subTypeOptions.length > 0 && (
				<Controller
					control={control}
					name={exactPropertyTypeName as keyof Filters}
					rules={{ required: exactTypeRequired ? "Please select exact property type" : false }}
					render={({ field: { value, onChange, onBlur }, fieldState }) => (
						<RadioGroupInput
							className="ml-2"
							label="Exact property type"
							labelClassName="text-title"
							options={subTypeOptions}
							value={value as PropertyType}
							onChange={onChange}
							itemsClassName="grid grid-cols-1 md:grid-cols-3"
							onBlur={onBlur}
							errorMessage={fieldState.error?.message}
							// description="Select the exact subtype of your property (e.g., 2 BHK, Independent House, Luxury Villa) based on the main type selected. This helps buyers better understand the listing."
						/>
					)}
				/>
			)}
			{/* Community Switch - Show when Project/Community is selected */}
			{exactTypeValue === "h:p-" && isCommunityFieldName && (
				<SwitchField
					control={control}
					name={isCommunityFieldName as keyof Filters}
					label="Is Community?"
					className="ml-2"
				/>
			)}
		</div>
	);
}
